/**
 * PermissionManager Component
 * 
 * Componente para gerenciar permissões granulares de grupos de acesso.
 * Permite configurar permissões por módulo e ação.
 * 
 * Funcionalidades:
 * - Visualização hierárquica de módulos e ações
 * - Checkboxes para habilitar/desabilitar permissões
 * - Seleção em massa (selecionar todas, desmarcar todas)
 * - Salvamento de permissões
 */

import { useState, useEffect } from 'react'
import { Check, ChevronRight, ChevronDown, Search } from 'lucide-react'
import { MODULES, MODULE_ACTIONS, MODULE_LABELS, actionStorageModule } from '../../../utils/permissions'
import './PermissionManager.css'

function clonePermissions(permissions) {
  return JSON.parse(JSON.stringify(permissions || {}))
}

function isActionEnabled(permissions, module, action) {
  const bucket = actionStorageModule(action, module)
  return permissions[bucket]?.[action.key] === true
}

function PermissionManager({ permissions = {}, onChange }) {
  const [localPermissions, setLocalPermissions] = useState({})
  const [expandedModules, setExpandedModules] = useState([])
  const [search, setSearch] = useState('')
  const [isDirty, setIsDirty] = useState(false)
  const query = search.trim().toLowerCase()

  // Sincronizar com props apenas quando não há edição pendente
  useEffect(() => {
    if (!isDirty) {
      setLocalPermissions(clonePermissions(permissions))
    }
  }, [permissions, isDirty])

  // Toggle de expansão de módulo
  const toggleModule = (module) => {
    setExpandedModules(prev =>
      prev.includes(module)
        ? prev.filter(m => m !== module)
        : [...prev, module]
    )
  }

  // Toggle de permissão individual
  const togglePermission = (module, action) => {
    const newPermissions = clonePermissions(localPermissions)
    const bucket = actionStorageModule(action, module)

    if (!newPermissions[bucket]) {
      newPermissions[bucket] = {}
    }

    const isEnabled = newPermissions[bucket][action.key] === true
    newPermissions[bucket][action.key] = !isEnabled

    setIsDirty(true)
    setLocalPermissions(newPermissions)

    if (onChange) {
      onChange(newPermissions)
    }
  }

  // Selecionar todas as permissões de um módulo
  const selectAllModule = (module) => {
    const newPermissions = clonePermissions(localPermissions)
    const actions = MODULE_ACTIONS[module] || []

    if (!newPermissions[module]) {
      newPermissions[module] = {}
    }

    const allSelected = actions.every(action => isActionEnabled(newPermissions, module, action))

    actions.forEach(action => {
      const bucket = actionStorageModule(action, module)
      if (!newPermissions[bucket]) newPermissions[bucket] = {}
      newPermissions[bucket][action.key] = !allSelected
    })

    setIsDirty(true)
    setLocalPermissions(newPermissions)

    if (onChange) {
      onChange(newPermissions)
    }
  }

  // Selecionar todas as permissões de todos os módulos
  const selectAll = () => {
    const newPermissions = clonePermissions(localPermissions)
    const allSelected = Object.values(MODULES).every(module => {
      const actions = MODULE_ACTIONS[module] || []
      return actions.every(action => isActionEnabled(newPermissions, module, action))
    })

    Object.values(MODULES).forEach(module => {
      if (!newPermissions[module]) {
        newPermissions[module] = {}
      }
      const actions = MODULE_ACTIONS[module] || []
      actions.forEach(action => {
        const bucket = actionStorageModule(action, module)
        if (!newPermissions[bucket]) newPermissions[bucket] = {}
        newPermissions[bucket][action.key] = !allSelected
      })
    })

    setIsDirty(true)
    setLocalPermissions(newPermissions)

    if (onChange) {
      onChange(newPermissions)
    }
  }

  // Verificar se módulo está totalmente selecionado
  const isModuleFullySelected = (module) => {
    const actions = MODULE_ACTIONS[module] || []
    if (actions.length === 0) return false
    
    return actions.every(action => isActionEnabled(localPermissions, module, action))
  }

  // Verificar se módulo tem alguma permissão selecionada
  const hasModuleAnySelected = (module) => {
    const actions = MODULE_ACTIONS[module] || []
    return actions.some(action => isActionEnabled(localPermissions, module, action))
  }

  const moduleMatchesQuery = (module) => {
    if (!query) return true
    const label = (MODULE_LABELS[module] || module).toLowerCase()
    if (label.includes(query)) return true
    return (MODULE_ACTIONS[module] || []).some(action =>
      (action.label || '').toLowerCase().includes(query) ||
      (action.group || '').toLowerCase().includes(query)
    )
  }

  const visibleActions = (module, actions) => {
    if (!query) return actions
    const label = (MODULE_LABELS[module] || module).toLowerCase()
    if (label.includes(query)) return actions
    return actions.filter(action =>
      (action.label || '').toLowerCase().includes(query) ||
      (action.group || '').toLowerCase().includes(query)
    )
  }

  const visibleModules = Object.values(MODULES).filter(moduleMatchesQuery)

  return (
    <div className="permission-manager">
      <div className="permission-manager-toolbar">
        <button
          type="button"
          className="btn-select-all"
          onClick={selectAll}
        >
          {Object.values(MODULES).every(module => isModuleFullySelected(module))
            ? 'Desmarcar Todas'
            : 'Selecionar Todas'}
        </button>
        <label className="permission-search">
          <Search size={16} />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar módulo ou permissão"
            aria-label="Buscar módulo ou permissão"
          />
        </label>
      </div>

      <div className="permission-manager-content">
        {visibleModules.map(module => {
          const allActions = MODULE_ACTIONS[module] || []
          const actions = visibleActions(module, allActions)
          const isExpanded = query ? true : expandedModules.includes(module)
          const isFullySelected = isModuleFullySelected(module)
          const hasAnySelected = hasModuleAnySelected(module)

          return (
            <div key={module} className="permission-module">
              <div
                className={`permission-module-header ${hasAnySelected ? 'has-permissions' : ''}`}
                onClick={() => toggleModule(module)}
              >
                <div className="permission-module-title">
                  <span className="permission-module-icon">
                    {isExpanded ? (
                      <ChevronDown size={18} />
                    ) : (
                      <ChevronRight size={18} />
                    )}
                  </span>
                  <span className="permission-module-label">
                    {MODULE_LABELS[module]}
                  </span>
                  {hasAnySelected && (
                    <span className="permission-badge">
                      {allActions.filter(a => isActionEnabled(localPermissions, module, a)).length} / {allActions.length}
                    </span>
                  )}
                </div>
                <div className="permission-module-actions">
                  <button
                    type="button"
                    className="btn-module-select-all"
                    onClick={(e) => {
                      e.stopPropagation()
                      selectAllModule(module)
                    }}
                  >
                    {isFullySelected ? 'Desmarcar Todas' : 'Selecionar Todas'}
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className="permission-module-content">
                  {(() => {
                    // Agrupar ações por grupo (sub-módulo)
                    const groupedActions = {}
                    actions.forEach(action => {
                      const group = action.group || 'Geral'
                      if (!groupedActions[group]) {
                        groupedActions[group] = []
                      }
                      groupedActions[group].push(action)
                    })

                    // Renderizar grupos
                    return Object.entries(groupedActions).map(([groupName, groupActions]) => (
                      <div key={groupName} className="permission-submodule-group">
                        {groupName !== 'Geral' && groupActions.length > 0 && Object.keys(groupedActions).length > 1 && (
                          <div className="permission-submodule-header">
                            <span className="permission-submodule-title">{groupName}</span>
                          </div>
                        )}
                        {groupActions.map(action => {
                          const isChecked = isActionEnabled(localPermissions, module, action)

                          return (
                            <label
                              key={`${actionStorageModule(action, module)}-${action.key}`}
                              className={`permission-action ${isChecked ? 'checked' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => togglePermission(module, action)}
                              />
                              <span className="permission-action-label">
                                {action.label}
                              </span>
                              {isChecked && (
                                <Check size={16} className="permission-check-icon" />
                              )}
                            </label>
                          )
                        })}
                      </div>
                    ))
                  })()}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {query && visibleModules.length === 0 && (
        <div className="permission-manager-empty">
          <p>Nenhum módulo encontrado para “{search.trim()}”.</p>
        </div>
      )}

      {!query && Object.keys(localPermissions).length === 0 && (
        <div className="permission-manager-empty">
          <p>Nenhuma permissão configurada. Use os controles acima para configurar as permissões.</p>
        </div>
      )}
    </div>
  )
}

export default PermissionManager

